import { useModalStatusMessage } from '@/hooks';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import UPDATE_USER_COLLECTION_RELATIONS from '@features/access/graphql/mutations/updateUserCollectionRelations';
import { CollectionsAccessForUserInput, CollectionUserValue } from '@features/collectionsAccessInputs';
import GET_COLLECTIONS_FOR_USER_ACCESS from '@features/collectionsAccessInputs/graphql/queries/getCollectionsForUserAccess';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Typography } from '@mui/material';
import { useEffect, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import GET_ALL_DEVICE_ACCESS_FOR_USER from '../allAccessTab/graphql/queries/getAllDeviceAccessForUser';
import { TabContent, TabProps } from '../common';
import GET_USER_MAIN_ORGANIZATION from './getUserMainOrganization';

type FormValues = {
  userId: number;
  organizationIds: number[];
  updatedRelations: CollectionUserValue[];
};

export const CollectionsAccessTab: FC<TabProps> = ({ userId, onClose, setIsDirty }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const { data } = useSuspenseQuery(GET_USER_MAIN_ORGANIZATION, {
    variables: { userId },
  });
  const mainOrganizationId = data.user.mainOrganization?.organization.id;

  const methods = useForm<FormValues>({
    defaultValues: {
      userId,
      organizationIds: mainOrganizationId != null ? [mainOrganizationId] : [],
      updatedRelations: [],
    },
  });

  useEffect(() => setIsDirty(methods.formState.isDirty), [methods.formState.isDirty, setIsDirty]);

  const [updateRelations] = useMutation(UPDATE_USER_COLLECTION_RELATIONS, {
    refetchQueries: [
      {
        query: GET_COLLECTIONS_FOR_USER_ACCESS,
        variables: { organizationIds: methods.getValues('organizationIds'), accountId: userId },
      },
      // AMARU-562: el tab "Todos los accesos" agrega este mismo dato — sin esto queda desactualizado
      // tras guardar acá, aunque el usuario no lo tenga montado en este momento.
      { query: GET_ALL_DEVICE_ACCESS_FOR_USER, variables: { userId } },
    ],
    onCompleted() {
      methods.reset({
        userId,
        organizationIds: methods.getValues('organizationIds'),
        updatedRelations: [],
      });
      openModalSuccessMessage(_(msg`Relaciones actualizadas correctamente`));
    },
    onError(error) {
      openModalErrorMessage(error.message);
    },
  });

  const onSubmit = methods.handleSubmit((formData) => {
    updateRelations({
      variables: {
        accountId: userId,
        inputs: formData.updatedRelations,
      },
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <TabContent
        slotProps={{
          modalActions: {
            onClose,
            onSubmit,
            onResetForm: () => methods.reset(),
            submitLoading: methods.formState.isSubmitting,
            dirtyForm: methods.formState.isDirty,
          },
        }}
      >
        <Typography variant="h6" mb={2}>
          <Trans>Colecciones a las que tiene acceso el usuario</Trans>
        </Typography>
        <Controller
          control={methods.control}
          name="organizationIds"
          render={({ field: { ref, ...orgFieldProps } }) => (
            <Controller
              control={methods.control}
              name="updatedRelations"
              render={({ field: { ref: relationsRef, ...relationsFieldProps } }) => (
                <CollectionsAccessForUserInput
                  height={380}
                  userId={userId}
                  organizationIds={orgFieldProps.value}
                  onOrganizationIdsChange={orgFieldProps.onChange}
                  value={relationsFieldProps.value}
                  onChange={relationsFieldProps.onChange}
                />
              )}
            />
          )}
        />
      </TabContent>
    </form>
  );
};
